import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-we360-ai";

const FAQ_ITEMS = [
  {
    question: "How is TeamGrid different from We360.ai?",
    answer:
      "TeamGrid is built to help leaders understand why work happens the way it does using AI to surface trends, blockers, and areas for improvement. We360.ai focuses on time and activity tracking. TeamGrid replaces monitoring with clarity and action, making it the Best We360.ai Alternative for growing businesses.",
  },
  {
    question: "What is Ask TeamGrid?",
    answer:
      "Ask TeamGrid is an AI-powered assistant that helps managers ask practical questions like: What's slowing us down? Who's overloaded right now? Where can we improve team efficiency? It replaces time-consuming dashboards with instant clarity, one reason it's seen as a top choice among We360.ai alternatives for businesses in India.",
  },
  {
    question: "Is TeamGrid scalable for enterprise use?",
    answer:
      "Yes. TeamGrid supports multi-team, cross-department visibility without overwhelming management or staff. It's a reliable We360.ai alternative for both startups and large organizations.",
  },
  {
    question: "Does TeamGrid work for remote and hybrid teams?",
    answer:
      "Absolutely. TeamGrid is designed to work seamlessly across all work models — remote, hybrid, or in-office — offering consistent visibility without requiring behavior-level tracking.",
  },
  {
    question: "Is there a Free version or trial of TeamGrid?",
    answer:
      "Yes! TeamGrid offers a full-featured Free Trial, making it the Best Free We360.ai alternative for teams that want to try insight-led management before committing.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "How is TeamGrid different from We360.ai?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is built to help leaders understand why work happens the way it does using AI to surface trends, blockers, and areas for improvement. We360.ai focuses on time and activity tracking. TeamGrid replaces monitoring with clarity and action for insight-led teams.",
      },
    },
    {
      "@type": "Question",
      name: "What is Ask TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ask TeamGrid is an AI-powered assistant that helps managers ask practical questions like what's slowing the team down, who's overloaded, and where efficiency can improve. It replaces time-consuming dashboard analysis with instant, downloadable summaries.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid scalable for enterprise use?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid supports multi-team, cross-department visibility without overwhelming management or staff, making it suitable for both startups and large organizations.",
      },
    },
    {
      "@type": "Question",
      name: "Does TeamGrid work for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. TeamGrid works across remote, hybrid, and in-office teams and provides consistent visibility without requiring behavior-level tracking.",
      },
    },
    {
      "@type": "Question",
      name: "Is there a Free version or trial of TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid offers a full-featured free trial so teams can try insight-led management before committing.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "Looking for a Better We360.ai Alternative? Meet TeamGrid",
      description: "TeamGrid helps leaders understand how work really flows with AI-powered insights—making it a privacy-first We360.ai alternative for teams that value outcomes over oversight.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://we360.ai/#we360-app" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs We360.ai", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      description: "TeamGrid is an AI-powered workforce intelligence platform that helps leaders understand how and why work happens using privacy-first insights, summaries, and organizational intelligence—without employee surveillance.",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      featureList: [
        "Outcome-led, pattern-based visibility (understand work flow)",
        "AI-powered insights to identify patterns, risks, blockers, and opportunities",
        "Ask TeamGrid (conversational AI) for instant summaries and answers",
        "Privacy-first design focused on trust and enablement",
        "Organizational Intelligence Engine surfaces friction, burnout signals, and inefficiencies",
        "Daily, hourly, and weekly work summaries",
        "One-click, shareable reports",
        "Scales across remote, hybrid, and in-office teams with fast onboarding",
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://we360.ai/#we360-app",
      name: "We360.ai",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, Web",
      description: "We360.ai is a time and activity monitoring platform focused on tracking productivity, app usage, and behavior-based visibility with reports and exports.",
      url: "https://we360.ai/",
      publisher: { "@type": "Organization", name: "We360.ai", url: "https://we360.ai/" },
      featureList: [
        "Time, app usage, and behavior-based visibility",
        "Productivity analytics and engagement metrics",
        "Reports such as daily/weekly/monthly time-based reporting",
        "Exports in Excel and other formats",
        "Monitoring features such as activity tracking",
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs We360.ai - Feature Comparison",
      description: "Comparison of TeamGrid and We360.ai across philosophy, AI insights, conversational AI, privacy approach, visibility model, dashboards, organizational intelligence, summaries, and reporting.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://we360.ai/#we360-app" } },
      ],
    },
  ],
};

const WE360_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Understand how and why work happens", competitor: "Monitor time, activity and productivity" },
  { feature: "AI-Powered Insights", teamgrid: "Automatically identifies patterns, risks, and blockers", competitor: "Manual interpretation of collected data" },
  { feature: "Ask TeamGrid (AI Chat)", teamgrid: "Ask TeamGrid for instant summaries and answers", competitor: "Not available" },
  { feature: "Privacy-Oriented Design", teamgrid: "Focused on trust, visibility, and team enablement", competitor: "Built with monitoring features such as activity tracking" },
  { feature: "Approach to Visibility", teamgrid: "Outcome-led, pattern-based", competitor: "Time, app usage, and behavior-based" },
  { feature: "Dashboards", teamgrid: "Clean, real-time dashboards with AI summaries", competitor: "Real-time insights on productivity, behavior, and time" },
  { feature: "Organizational Insights", teamgrid: "Surfaces friction, burnout signals and inefficiencies", competitor: "Productivity analytics and engagement metrics" },
  { feature: "Work Summaries", teamgrid: "Daily, hourly, and weekly summaries", competitor: "Daily, weekly, monthly time-based reports" },
  { feature: "Export & Sharing", teamgrid: "One-click, shareable reports", competitor: "Export reports in Excel and other formats" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Provides visibility through aggregated work patterns and AI-driven summaries",
  "Teams adopt it faster thanks to its non-invasive, insight-first design",
  "Leaders get the clarity they need without disrupting how teams work",
  "That's why growing companies across sectors are looking for We360.ai alternatives for businesses in India that promote performance through understanding not just tracking.",
];

const DATA_DECISIONS_WE360_BULLETS = [
  "Focuses on monitoring behavior through activity levels, app usage and productivity scores",
  "Can create pushback from teams, especially in trust-sensitive cultures",
  "Requires detailed rollout and training to align with employee expectations",
];

const DATA_DECISIONS_2_TEAMGRID_BULLETS = [
  "AI highlights workflow blockers, slowdowns, and team imbalances",
  "Ask TeamGrid gives real-time answers without needing to comb through dashboards",
  "Reduces reporting fatigue and accelerates leadership decision-making",
];

const DATA_DECISIONS_2_WE360_BULLETS = [
  "Tracks apps, websites, and employee engagement metrics",
  "Provides data, but leaves interpretation to the manager",
  "Insight and action depend on manual follow-up",
];

const WE360_WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid (AI chat for reports and insights)",
  "Organizational Intelligence Engine",
  "Real-time dashboards",
  "Daily, hourly, and weekly summaries",
  "One-click, presentation ready reports",
  "Seamless onboarding and fast adoption",
  "Designed as a trust-driven We360.ai alternative",
  "Scales easily across remote, hybrid and office teams",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid – Your AI Workforce Assistant",
    intro: "Managers get instant answers to what's slowing the team, who's overloaded, and where efficiency can improve—with downloadable summaries in seconds.",
    bullets: [],
    closing: "",
  },
  {
    title: "Organizational Intelligence, Not Just Monitoring",
    intro: "TeamGrid surfaces friction, burnout signals, and inefficiencies automatically—without behavior-level tracking or surveillance.",
    bullets: [],
    closing: "",
  },
];

const We360Comparison = () => (
  <ComparisonPageTemplate
    seoTitle="We360.ai Alternative for Modern Teams | TeamGrid"
    seoDescription="Looking for a We360.ai alternative for your business? TeamGrid offers insight-led workforce intelligence without monitoring pressure."
    seoCanonical={CANONICAL_URL}
    jsonLdGraph={JSON_LD_GRAPH}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleLine1="Looking for a Better We360.ai Alternative?"
    heroTitleLine2="Meet TeamGrid"
    heroTitleLine2Teal
    heroTitleLine3={null}
    heroTitleLine4={null}
    heroDescription="TeamGrid helps leaders understand how work really flows with AI-powered insights, making it the We360.ai alternative for teams that value outcomes over oversight."
    competitorName="We360.ai"
    competitorCardLabel="We360.ai"
    competitorLogoUrl="/Background+Border_we360.webp"
    trustedBadges={[
      "Real workforce insights not just activity logs",
      "Privacy-first by design",
      "Ask TeamGrid: simple AI chat for instant team reports",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. We360.ai."
    comparisonTableRows={WE360_TABLE_ROWS}
    dataDecisionsHeading="Use Case 1: Insight Without Invasiveness"
    dataDecisionsSubtitle="Why Organizations Choose the Best We360.ai Alternative for Modern Team Visibility"
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_WE360_BULLETS}
    dataDecisionsCompetitorCardTitle="We360.ai"
    dataDecisions2Heading="Use Case 2: From Monitoring Data to Meaningful Decisions"
    dataDecisions2Subtitle="Why Teams Look for the Top We360.ai Alternative That Moves Beyond Activity Logs"
    dataDecisions2TeamGridBullets={DATA_DECISIONS_2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS_2_WE360_BULLETS}
    dataDecisions2CompetitorCardTitle="We360.ai"
    keyFeaturesTitle="Why Teams Choose TeamGrid Over We360.ai"
    keyFeaturesSubtitle="Ask TeamGrid – Your AI Workforce Assistant"
    keyFeatureCards={KEY_FEATURE_CARDS}
    whatYouGetItems={WE360_WHAT_YOU_GET_ITEMS}
    outcomesTitle="Performance Outcomes Teams See"
    outcomesImageSrc="/Section_2.webp"
  />
);

export default We360Comparison;
